import { SimpleGrid } from '@chakra-ui/react';

import { SectionCard, ValidatedField, CurrencyInput } from '../../components';
import {
  useValidationSchema,
  useValidationEntity,
  ValidationEntityProvider,
} from '../../hooks';
import type { Location } from 'api';

interface CoverageAmountsCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
}

const CoverageAmountsCardContent = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
}: CoverageAmountsCardProps) => {
  const { isFieldVisible } = useValidationSchema();
  const entity = useValidationEntity() ?? {};

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;

  const handleCurrencyChange =
    (field: keyof Location) => (_: string, value: number | null) => {
      onUpdate(locationIndex, field, value);
    };

  const showBuildingValue = isFieldVisible(
    'location',
    'building_value',
    entity,
  );
  const showBuildingCoverage = isFieldVisible(
    'location',
    'building_coverage',
    entity,
  );
  const showOutbuildings = isFieldVisible('location', 'outbuildings', entity);

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      {showBuildingValue && (
        <ValidatedField
          itemPath="location"
          fieldPath="building_value"
          label="Building Value"
          isDisabled={isUpdating || isFieldLocked('building_value')}
        >
          <CurrencyInput
            value={location.building_value ?? ''}
            onChange={handleCurrencyChange('building_value')}
            isDisabled={isUpdating || isFieldLocked('building_value')}
            showCurrencySymbol
            height={38}
          />
        </ValidatedField>
      )}

      {showBuildingCoverage && (
        <ValidatedField
          itemPath="location"
          fieldPath="building_coverage"
          label="Building Coverage"
          isDisabled={isUpdating || isFieldLocked('building_coverage')}
        >
          <CurrencyInput
            value={location.building_coverage ?? ''}
            onChange={handleCurrencyChange('building_coverage')}
            isDisabled={isUpdating || isFieldLocked('building_coverage')}
            showCurrencySymbol
            height={38}
          />
        </ValidatedField>
      )}

      {showOutbuildings && (
        <ValidatedField
          itemPath="location"
          fieldPath="outbuildings"
          label="Outbuildings"
          isDisabled={isUpdating}
        >
          <CurrencyInput
            value={location.outbuildings ?? ''}
            onChange={handleCurrencyChange('outbuildings')}
            isDisabled={isUpdating}
            showCurrencySymbol
            height={38}
            placeholder="BASE"
          />
        </ValidatedField>
      )}

      <ValidatedField
        itemPath="location"
        fieldPath="contents"
        label="Contents"
        isDisabled={isUpdating}
      >
        <CurrencyInput
          value={location.contents ?? ''}
          onChange={handleCurrencyChange('contents')}
          isDisabled={isUpdating}
          showCurrencySymbol
          height={38}
          placeholder="BASE"
        />
      </ValidatedField>
    </SimpleGrid>
  );
};

const CoverageAmountsCard = (props: CoverageAmountsCardProps) => {
  return (
    <ValidationEntityProvider
      entity={props.location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Coverage Amounts">
        <CoverageAmountsCardContent {...props} />
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default CoverageAmountsCard;
